import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  margin-top: 180px;
  background: #ffffff;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin-top: 155px;
  }


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

/* ========== HERO ========== */
const HeroSection = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #3730A3 50%, #0b0b77 100%);
  color: white;
  padding: 3rem 5%;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 2.8rem; font-weight: 800; margin-bottom: 0.3rem;
  span { color: #a78bfa; }
  @media (max-width: 768px) { font-size: 2rem; }
`;

const HeroLine = styled.div`
  width: 60px; height: 3px; background: #a78bfa; margin: 0.8rem auto 1rem; border-radius: 2px;
`;

const HeroDesc = styled.p`
  font-size: 1rem; opacity: 0.85; max-width: 550px; margin: 0 auto;
`;

/* ========== CONTAINER ========== */
const Container = styled.div`
  max-width: 1200px; margin: 0 auto; padding: 3rem 5%;
`;

/* ========== FILTER ========== */
const FilterBar = styled.div`
  display: flex; justify-content: center; gap: 0.6rem; flex-wrap: wrap; margin-bottom: 3rem;
`;

const FilterBtn = styled.button`
  font-family: 'Times New Roman', serif;
  padding: 0.5rem 1.5rem; border-radius: 6px; border: 1px solid ${props => props.active ? '#3730A3' : '#d0d0d8'};
  background: ${props => props.active ? '#3730A3' : 'transparent'}; color: ${props => props.active ? 'white' : '#4a4a6a'};
  font-size: 0.9rem; cursor: pointer; transition: all 0.3s ease; letter-spacing: 0.3px;
  &:hover { border-color: #3730A3; color: ${props => props.active ? 'white' : '#3730A3'}; }
`;

/* ========== GALLERY GRID ========== */
const GalleryGrid = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 1.5rem;
  @media (max-width: 1000px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 700px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 450px) { grid-template-columns: 1fr; }
`;

const GalleryCard = styled.div`
  background: white; border-radius: 8px; overflow: hidden;
  border: 1px solid #e8e8ec; transition: all 0.4s ease; cursor: pointer;
  position: relative;

  &:hover { 
    transform: translateY(-6px); 
    box-shadow: 0 15px 35px rgba(55,48,163,0.1);
    
    .overlay { opacity: 1; }
  }
`;

const CardImageWrap = styled.div`
  height: 280px; background: #f5f3ff; position: relative; overflow: hidden;
  
  img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s ease; }
  ${GalleryCard}:hover & img { transform: scale(1.05); }
`;

const CardOverlay = styled.div`
  position: absolute; inset: 0; background: linear-gradient(to top, rgba(55,48,163,0.85), rgba(55,48,163,0.2));
  display: flex; align-items: flex-end; justify-content: center; padding: 1.5rem;
  opacity: 0; transition: opacity 0.3s ease;
  color: white; text-align: center;

  div { 
    font-size: 0.85rem; line-height: 1.4;
    strong { display: block; font-size: 0.95rem; margin-bottom: 0.2rem; }
  }
`;

const CardInfo = styled.div`
  padding: 1rem; text-align: center; border-top: 3px solid #3730A3;
  h4 { font-size: 1rem; color: #1a1a2e; font-weight: 700; margin-bottom: 0.15rem; }
  p { font-size: 0.8rem; color: #4a4a6a; }
`;

/* ========== LIGHTBOX ========== */
const Lightbox = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.92); z-index: 10000;
  display: ${props => props.open ? 'flex' : 'none'}; align-items: center; justify-content: center;
  padding: 2rem; cursor: pointer;
`;

const LightboxInner = styled.div`
  max-width: 500px; width: 100%; text-align: center; color: white; cursor: default;
  img { width: 100%; max-height: 450px; object-fit: contain; border-radius: 8px; margin-bottom: 1.2rem; border: 3px solid rgba(255,255,255,0.15); }
  h3 { font-size: 1.4rem; font-weight: 700; margin-bottom: 0.2rem; }
  p { opacity: 0.75; font-size: 0.95rem; }
  .lightbox-line { width: 40px; height: 2px; background: #a78bfa; margin: 0.8rem auto; }
`;

const CloseText = styled.span`
  position: absolute; top: 2rem; right: 2rem; color: white; font-size: 1rem;
  cursor: pointer; opacity: 0.7; letter-spacing: 1px;
  &:hover { opacity: 1; }
`;

const Gallery = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [filter, setFilter] = useState('All');
  const [lightbox, setLightbox] = useState(null);

  const categories = ['All', 'CCS', 'CDI', 'CPC', 'CDIP'];

  const students = [
    { name: "Priya Sharma", cert: "CCS Certified", year: "2024", image: "/images/students/student1.png", category: "CCS" },
    { name: "Rahul Verma", cert: "CDI Specialist", year: "2024", image: "/images/students/student2.png", category: "CDI" },
    { name: "Neha Gupta", cert: "CPC Certified", year: "2023", image: "/images/students/student3.png", category: "CPC" },
    { name: "Arun Kumar", cert: "CCS & CDIP", year: "2024", image: "/images/students/student4.png", category: "CCS" },
    { name: "Sneha Reddy", cert: "CDI Specialist", year: "2023", image: "/images/students/student5.png", category: "CDI" },
    { name: "Vikram Singh", cert: "CCS Certified", year: "2024", image: "/images/students/student6.png", category: "CCS" },
    { name: "Ananya Patel", cert: "CPC & CCS", year: "2024", image: "/images/students/student7.png", category: "CPC" },
    { name: "Mohammed Ali", cert: "CDI Specialist", year: "2023", image: "/images/students/student8.png", category: "CDI" },
    { name: "Kavita Joshi", cert: "CCS Certified", year: "2024", image: "/images/students/student9.png", category: "CCS" },
    { name: "Rajesh Menon", cert: "CPC Certified", year: "2024", image: "/images/students/student10.png", category: "CPC" },
    { name: "Deepika Shah", cert: "CDI Specialist", year: "2023", image: "/images/students/student11.png", category: "CDI" },
    { name: "Suresh Nair", cert: "CCS Certified", year: "2024", image: "/images/students/student12.png", category: "CCS" }
  ];

  const filteredStudents = filter === 'All' ? students : students.filter(s => s.category === filter);

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>Hall of <span>Achievers</span></HeroTitle>
        <HeroLine />
        <HeroDesc>Celebrating our certified graduates who achieved excellence in medical coding</HeroDesc>
      </HeroSection>

      <Container>
        <GalleryGrid>
          {filteredStudents.map((student, i) => (
            <GalleryCard key={i} onClick={() => setLightbox(student)}>
              <CardImageWrap>
                <img src={student.image} alt={student.name} onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.background = '#e8e8f0'; }} />
                <CardOverlay className="overlay">
                  <div><strong>{student.name}</strong>{student.cert}</div>
                </CardOverlay>
              </CardImageWrap>
              <CardInfo>
                <h4>{student.name}</h4>
                <p>{student.cert} — {student.year}</p>
              </CardInfo>
            </GalleryCard>
          ))}
        </GalleryGrid>
      </Container>

      <Lightbox open={!!lightbox} onClick={() => setLightbox(null)}>
        <CloseText>CLOSE</CloseText>
        {lightbox && (
          <LightboxInner onClick={e => e.stopPropagation()}>
            <img src={lightbox.image} alt={lightbox.name} onError={(e) => { e.target.style.display = 'none'; }} />
            <h3>{lightbox.name}</h3>
            <div className="lightbox-line"></div>
            <p>{lightbox.cert} — Batch of {lightbox.year}</p>
          </LightboxInner>
        )}
      </Lightbox>
    </PageContainer>
  );
};

export default Gallery;